import React, { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

export const NDS_STORAGE_KEY = "nationalDogSurvey";
export const WARM_TEST_DATA: NDS.WarmUserData = {
	dog: {
		name: "Buddy",
		age: "5",
		dogSex: "Male",
		monthAcquired: "1",
		yearAcquired: "2020",
		neuterStatus: "Neutered",
	},
	firstName: "Bob",
	supporterId: "123",
};

// Utility functions for localStorage with expiration
const getLocalNDSData = () => {
	try {
		const rawData = localStorage.getItem(NDS_STORAGE_KEY);
		if (!rawData) return null;

		const data = JSON.parse(rawData);

		// Check if the data has expired (72 hours = 3 days = 259200000 ms)
		if (data._timestamp && Date.now() - data._timestamp > 259200000) {
			// Data is expired, clear it and return null
			localStorage.removeItem(NDS_STORAGE_KEY);
			return null;
		}

		// Return data without the timestamp
		const { _timestamp, ...actualData } = data;
		return JSON.stringify(actualData);
	} catch (error) {
		console.error("Error reading from localStorage", error);
		return null;
	}
};

const setLocalNDSData = (data: string) => {
	try {
		// Add timestamp to the data
		const dataWithTimestamp = {
			...JSON.parse(data),
			_timestamp: Date.now(),
		};
		localStorage.setItem(NDS_STORAGE_KEY, JSON.stringify(dataWithTimestamp));
	} catch (error) {
		console.error("Error writing to localStorage", error);
	}
};

export const DEFAULT_ROUTE_SCREEN: NDS.ScreenIndex = {
	route: "ABOUT_YOUR_DOGS",
	screen: "AYD_NOD_INTRO",
};

export const DEFAULT_WARM_ROUTE_SCREEN: NDS.ScreenIndex = {
	route: "WARM",
	screen: "W0",
};

export const DEFAULT_ROUTE_STACK: NDS.ScreenState[] = [
	{
		...DEFAULT_ROUTE_SCREEN,
		dogCount: 0,
		dogsState: {},
	},
];

export const DEFAULT_SURVEY_PROGRESS: NDS.SurveyProgress = {
	ABOUT_YOUR_DOGS: 0,
	CHOOSING_YOUR_DOG: -1,
	UNDERSTANDING_YOUR_DOG: -1,
	RELATIONSHIP_WITH_YOUR_DOG: -1,
	ABOUT_YOU: -1,
};

export const DEFAULT_SURVEY_STATE: NDS.SurveyState = {
	warmData: null,
	surveyProgress: DEFAULT_SURVEY_PROGRESS,
	routeStack: DEFAULT_ROUTE_STACK,
	playedTransitions: [],
	surveyStartTime: Date.now(),
	hasSubmitted: false,
	surveyResponseId: uuidv4(),
};

export const usePersistSurveyData = (
	formData: NDS.FormData,
	setFormData: (formData: NDS.FormData) => void,
	fieldsSeen: string[],
	setFieldsSeen: (fieldsSeen: string[]) => void,
	setTransition: (transition: NDS.TransitionTrigger | null) => void,
) => {
	// /* All other */ survey state
	const [surveyState, setSurveyState] =
		useState<NDS.SurveyState>(DEFAULT_SURVEY_STATE);

	const [hasInitialisedFromCookies, setHasInitialisedFromCookies] =
		useState(false);

	const clearSurveyCookies = useCallback(() => {
		const storageValue = JSON.stringify({
			formData: {},
			surveyState: DEFAULT_SURVEY_STATE,
			fieldsSeen: [],
		});
		setLocalNDSData(storageValue);
	}, []);

	const exportSurveyCookies = useCallback(() => {
		const storageData = {
			formData,
			surveyState,
			fieldsSeen,
		};
		const storageValue = JSON.stringify(storageData, null, 2);
		navigator.clipboard.writeText(storageValue);
		return storageValue;
	}, [formData, surveyState, fieldsSeen]);

	const importSurveyCookies = useCallback(
		(storageValue: string) => {
			const storageData = JSON.parse(storageValue);
			setFormData(storageData.formData);
			setSurveyState(storageData.surveyState);
			setFieldsSeen(storageData.fieldsSeen || []);
			setHasInitialisedFromCookies(true);
			setTransition(null);
			window.scrollTo({ top: 0, behavior: "instant" });
		},
		[setFormData, setTransition, setFieldsSeen],
	);

	const resetSurvey = useCallback(() => {
		localStorage.removeItem(NDS_STORAGE_KEY);
	}, []);

	const restartSpecificRouteDebug = useCallback(
		(screenIndex: NDS.ScreenIndex, shouldInitialiseDogs = true) => {
			clearSurveyCookies();
			const newSurveyState: NDS.SurveyState = {
				...DEFAULT_SURVEY_STATE,

				routeStack: [
					{
						...DEFAULT_ROUTE_STACK[0],
						...screenIndex,
						dogCount: shouldInitialiseDogs ? 5 : 0,
						dogsState: shouldInitialiseDogs
							? {
									"Test Dog 1": {
										index: 1,
										isActive: true,
									},
									"Test Dog 2": {
										index: 2,
										isActive: false,
									},
									"Test Dog 3": {
										index: 3,
										isActive: false,
									},
									"Test Dog 4": {
										index: 4,
										isActive: false,
									},
									"Test Dog 5": {
										index: 5,
										isActive: false,
									},
								}
							: {},
					},
				],
			};

			setFormData({});
			setTransition(null);
			setFieldsSeen([]);
			setSurveyState(newSurveyState);
			setLocalNDSData(
				JSON.stringify({
					formData: {},
					surveyState: newSurveyState,
					fieldsSeen: [],
				}),
			);
			window.scrollTo({ top: 0, behavior: "instant" });
		},
		[clearSurveyCookies, setFormData, setTransition, setFieldsSeen],
	);
	const restartWarmRouteDebug = useCallback(
		(incomingWarmData: string | null) => {
			if (incomingWarmData === null) {
				console.log("No warm data provided");
				return;
			}
			clearSurveyCookies();
			const warmData: NDS.WarmUserData = JSON.parse(incomingWarmData);
			const cookieData: {
				formData: NDS.FormData;
				surveyState: NDS.SurveyState;
				fieldsSeen: string[];
			} = {
				formData: {},
				surveyState: {
					...DEFAULT_SURVEY_STATE,
					warmData: incomingWarmData?.length ? warmData : WARM_TEST_DATA,
					routeStack: [
						{
							...DEFAULT_ROUTE_STACK[0],
							...DEFAULT_WARM_ROUTE_SCREEN,
							dogCount: 1,
							dogsState: {
								[`${warmData.dog.name}*!*W`]: {
									index: 1,
									isActive: true,
									isWarm: true,
								},
							},
						},
					],
					surveyStartTime: Date.now(),
					surveyResponseId: uuidv4(),
				},
				fieldsSeen: [],
			};

			setLocalNDSData(JSON.stringify(cookieData));

			setFormData(cookieData.formData);
			setTransition(null);
			setSurveyState(cookieData.surveyState);
			setFieldsSeen([]);
			window.scrollTo({ top: 0, behavior: "instant" });
		},
		[clearSurveyCookies, setFormData, setTransition, setFieldsSeen],
	);
	const onLoadWarmRoute = useCallback(
		(
			incomingWarmData: NDS.WarmUserData,
			utmMediumParam: string,
			utmSourceParam: string,
		) => {
			// It's already been loaded and initialised so just use cookies.
			if (surveyState.warmData?.supporterId === incomingWarmData.supporterId) {
				return;
			}
			setFormData({
				utmMediumParam,
				utmSource: utmSourceParam,
				supporterId: incomingWarmData.supporterId,
			});
			setTransition(null);
			setFieldsSeen([]);
			setSurveyState({
				...DEFAULT_SURVEY_STATE,
				warmData: incomingWarmData,
				supporterId: incomingWarmData.supporterId,
				routeStack: [
					{
						...DEFAULT_ROUTE_STACK[0],
						...DEFAULT_WARM_ROUTE_SCREEN,
						dogsState: {
							[`${incomingWarmData.dog.name}*!*W`]: {
								index: 1,
								isActive: true,
								isWarm: true,
							},
						},
					},
				],
				surveyStartTime: Date.now(),
				surveyResponseId: uuidv4(),
			});
		},
		[
			surveyState.warmData?.supporterId,
			setFormData,
			setFieldsSeen,
			setTransition,
		],
	);

	// This effect keeps the localStorage in sync with the survey state
	useEffect(() => {
		if (!hasInitialisedFromCookies) {
			try {
				const data = getLocalNDSData();
				if (!data) {
					setHasInitialisedFromCookies(true);
					return;
				}
				const storageValue: {
					formData: NDS.FormData;
					surveyState: NDS.SurveyState;
					fieldsSeen: string[];
				} = JSON.parse(data);
				setFormData(storageValue.formData);
				setTransition(null);
				setSurveyState(storageValue.surveyState);
				setFieldsSeen(storageValue.fieldsSeen || []);
				setHasInitialisedFromCookies(true);
				return;
			} catch (error) {
				console.error("Error reading from localStorage", error);
				setHasInitialisedFromCookies(true);
				return;
			}
		}

		const storageData = {
			formData,
			surveyState,
			fieldsSeen,
		};

		const storageValue = JSON.stringify(storageData);

		setLocalNDSData(storageValue);
	}, [
		setFormData,
		setFieldsSeen,
		setTransition,
		formData,
		fieldsSeen,
		surveyState,
		hasInitialisedFromCookies,
	]);

	// This effect loads the survey state from localStorage on first render
	useEffect(() => {
		const storageValue = getLocalNDSData();
		if (storageValue) {
			importSurveyCookies(storageValue);
		} else {
			setHasInitialisedFromCookies(true);
		}
	}, [importSurveyCookies]);

	return {
		clearSurveyCookies,
		exportSurveyCookies,
		importSurveyCookies,
		onLoadWarmRoute,
		setSurveyState,
		setFormData,

		surveyState,
		hasInitialisedFromCookies,

		// Debugging
		resetSurvey,
		restartSpecificRouteDebug,
		restartWarmRouteDebug,
	};
};
